import { defineStore } from "pinia";
import { ref, computed } from "vue";

const SESSION_STORAGE_KEY = "chatbot_session_id";

const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

const getSessionIdFromStorage = () => {
  try {
    const storedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedSessionId) {
      return storedSessionId;
    }
  } catch (error) {
    console.error("Error reading session ID from storage:", error);
  }
  return null;
};

const saveSessionIdToStorage = (sessionId) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch (error) {
    console.error("Error saving session ID to storage:", error);
  }
};

export const useChatStore = defineStore("chat", () => {
  // State
  const messages = ref([]);
  const messageIdCounter = ref(0);
  const isLoading = ref(false);
  const sessionId = ref(getSessionIdFromStorage() || generateSessionId());

  // Getters
  const getMessages = computed(() => messages.value);
  const getMessageCount = computed(() => messages.value.length);
  const getIsLoading = computed(() => isLoading.value);
  const getSessionId = computed(() => sessionId.value);
  const getLastMessage = computed(() => {
    return messages.value.length > 0
      ? messages.value[messages.value.length - 1]
      : null;
  });

  // Actions
  const addMessage = (message) => {
    const newMessage = {
      id: messageIdCounter.value++,
      type: message.type,
      text: message.text,
      html: message.html || null,
      timestamp: new Date().toISOString(),
    };
    messages.value.push(newMessage);
    return newMessage;
  };

  const addUserMessage = (text) => {
    return addMessage({
      type: "user",
      text: text,
    });
  };

  const addBotMessage = (text, html = null) => {
    return addMessage({
      type: "bot",
      text: text,
      html: html,
    });
  };

  const setLoading = (loading) => {
    isLoading.value = loading;
  };

  const clearMessages = () => {
    messages.value = [];
    messageIdCounter.value = 0;
  };

  const resetSession = () => {
    const newSessionId = generateSessionId();
    sessionId.value = newSessionId;
    saveSessionIdToStorage(newSessionId);
    clearMessages();
  };

  // Save initial session ID to storage
  saveSessionIdToStorage(sessionId.value);

  const removeMessage = (messageId) => {
    const index = messages.value.findIndex((msg) => msg.id === messageId);
    if (index > -1) {
      messages.value.splice(index, 1);
    }
  };

  const updateMessage = (messageId, updates) => {
    const index = messages.value.findIndex((msg) => msg.id === messageId);
    if (index > -1) {
      messages.value[index] = { ...messages.value[index], ...updates };
    }
  };

  return {
    // State
    messages,
    messageIdCounter,
    isLoading,
    sessionId,
    // Getters
    getMessages,
    getMessageCount,
    getIsLoading,
    getSessionId,
    getLastMessage,
    // Actions
    addMessage,
    addUserMessage,
    addBotMessage,
    setLoading,
    clearMessages,
    removeMessage,
    updateMessage,
    resetSession,
  };
});
